import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "../lib/constants";
import { useSiteConfig } from "../lib/useSiteConfig";
import { resolveMediaUrl } from "../lib/mediaUrl";

const NAV_LINKS = [
    { to: "/",           label: "Home",            testId: "footer-link-home" },
    { to: "/about",      label: "About",           testId: "footer-link-about" },
    { to: "/services",   label: "Services",        testId: "footer-link-services" },
    { to: "/portfolio",  label: "Portfolio",       testId: "footer-link-portfolio" },
    { to: "/industries", label: "Industries",      testId: "footer-link-industries" },
    { to: "/print",      label: "Print Catalogue", testId: "footer-link-print" },
    { to: "/shop",       label: "Shop",            testId: "footer-link-shop" },
    { to: "/get-quote",  label: "Get a Quote",     testId: "footer-link-getquote" },
    { to: "/quote",      label: "Instant Estimate",testId: "footer-link-quote" },
    { to: "/blog",       label: "Blog",            testId: "footer-link-blog" },
    { to: "/faq",        label: "FAQ",             testId: "footer-link-faq" },
    { to: "/contact",    label: "Contact",         testId: "footer-link-contact" },
];

export const Footer = () => {
    const year = new Date().getFullYear();
    const config = useSiteConfig();
    const customLogo = config?.logo_url ? resolveMediaUrl(config.logo_url) : null;
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-black border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
                    {/* Column 1 — Brand */}
                    <div className="md:col-span-5">
                        <Link to="/" className="inline-flex items-center gap-2">
                            {customLogo ? (
                                <img
                                    src={customLogo}
                                    alt="Raj Graphics"
                                    className="h-10 md:h-12 w-auto max-w-[220px] object-contain"
                                    data-testid="footer-custom-logo"
                                />
                            ) : (
                                <>
                                    <div className="w-9 h-9 rounded-lg bg-black border border-gold/40 flex items-center justify-center">
                                        <span className="font-poppins font-extrabold text-gold text-lg leading-none">R</span>
                                    </div>
                                    <div className="leading-tight">
                                        <div className="font-poppins font-semibold text-white text-base">
                                            Raj <span className="text-gold">Graphics</span>
                                        </div>
                                        <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                            Design · Brand · Signage
                                        </div>
                                    </div>
                                </>
                            )}
                        </Link>
                        <p className="mt-6 font-inter text-sm text-zinc-400 max-w-md leading-relaxed">
                            A premium graphic design and branding studio crafting identities,
                            signage, and marketing assets for ambitious businesses across India.
                        </p>
                    </div>

                    {/* Column 2 — Navigation */}
                    <nav className="md:col-span-3" aria-label="Footer navigation">
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5">
                            Studio
                        </div>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-3 font-inter text-sm">
                            {NAV_LINKS.map((item) => (
                                <li key={item.to}>
                                    <Link
                                        to={item.to}
                                        data-testid={item.testId}
                                        className="text-zinc-300 hover:text-gold transition-colors whitespace-nowrap"
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* Column 3 — Contact */}
                    <div className="md:col-span-4">
                        <div className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5">
                            Get in touch
                        </div>
                        <ul className="space-y-4 font-inter text-sm">
                            <li>
                                <a
                                    href={`tel:${config?.phone_tel || PHONE_TEL}`}
                                    className="flex items-start gap-3 text-zinc-300 hover:text-gold transition-colors"
                                    data-testid="footer-phone"
                                >
                                    <Phone size={14} className="text-gold mt-0.5 flex-shrink-0" />
                                    <span>{config?.phone_display || PHONE_DISPLAY}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`mailto:${config?.email || EMAIL}`}
                                    className="flex items-start gap-3 text-zinc-300 hover:text-gold transition-colors break-all"
                                    data-testid="footer-email"
                                >
                                    <Mail size={14} className="text-gold mt-0.5 flex-shrink-0" />
                                    <span>{config?.email || EMAIL}</span>
                                </a>
                            </li>
                            <li className="flex items-start gap-3 text-zinc-400">
                                <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
                                <span>{config?.address || "Nagpur, Maharashtra · India"}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-white/5 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="font-inter text-xs text-zinc-500 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-2 gap-y-1">
                        <span>© {year} Raj Graphics. All rights reserved.</span>
                        <span className="text-zinc-700 hidden sm:inline">|</span>
                        <span>
                            Designed by{" "}
                            <span
                                className="font-semibold text-gold"
                                data-testid="footer-credit"
                            >
                                Husain Najmi
                            </span>
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-inter text-xs">
                        <Link
                            to="/privacy"
                            className="text-zinc-500 hover:text-gold transition-colors"
                            data-testid="footer-link-privacy"
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            to="/terms"
                            className="text-zinc-500 hover:text-gold transition-colors"
                            data-testid="footer-link-terms"
                        >
                            Terms &amp; Conditions
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
